import React, { useEffect, useState } from 'react'
import {
  Flex,
  Text,
  Button,
  FlexSpacer,
  useModalState,
  csx,
} from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'
import {
  Authorization,
  AuthorizationStatus,
} from '@/checkout-instore-legacy/transaction/authorization/Authorization'
import PaymentApp from '@/checkout-instore-legacy/transaction/authorization/challenge/paymentApp/PaymentApp'
import BottomModal from '@/checkout-instore-legacy/transaction/modal/BottomModal'
import OrderCancelModal from './OrderCancelModal'
import {
  cancelTransaction,
  setShouldStartTransaction,
  startTransaction,
} from '@/checkout-instore-legacy/transaction/redux/actions'
import { getReduxStore } from '@/checkout-instore-legacy/redux/reduxStore'
import { useSelector } from 'react-redux'
import PaymentRedirect from '@/checkout-instore-legacy/transaction/authorization/challenge/redirect/PaymentRedirect'
import Loading from './Loading'
import { removeFromLocalStorage } from '@/checkout-instore-legacy/utils/LocalStorageUtils'
import { setOrderActive } from '@/checkout-instore-legacy/order/actions'
import { sendMetric } from '@/checkout-instore-legacy/VtexLakeHouse'
import {
  MetricAction,
  MetricShowcase,
} from '@/checkout-instore-legacy/VtexLakeHouse/typings'
import { getOrderForm } from '@/checkout-instore-legacy/utils/orderFormUtils'

const messages = {
  PREPARING: 'placeorder.preparing.title',
  [AuthorizationStatus.PENDING]: 'placeorder.waiting.title',
  [AuthorizationStatus.FINISHED]: 'placeorder.finishing.title',
  [AuthorizationStatus.CANCELLED]: 'placeorder.finishing.title',
}

export interface PlacingOrderMapStateReturn {
  authorization?: Authorization
  isCancelling: boolean
  paymentApp: any
  redirectUrl: string
}

const PlacingOrder = () => {
  const { authorization, paymentApp, isCancelling, redirectUrl } = useSelector<
    any,
    PlacingOrderMapStateReturn
  >((state) => ({
    isCancelling: state.order.cancellationStatus.isCancelling,
    authorization: state.transaction.authorization,
    paymentApp: state.transaction.paymentApp,
    redirectUrl: state.transaction.redirectUrl,
  }))

  const loadingMessage = messages[authorization?.status ?? 'PREPARING']
  const dismissDialogModalState = useModalState()

  useEffect(() => {
    dismissDialogModalState.setOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isChanging, setIsChanging] = useState(false)
  const orderForm = getOrderForm()
  const showCancelModal = () => {
    dismissDialogModalState.setOpen(true)
  }

  const handleCancelTransaction = () => {
    getReduxStore().dispatch(cancelTransaction())
    dismissDialogModalState.toggle()
  }

  const handleResetTransaction = async () => {
    setIsChanging(true)
    const orderFormId = getReduxStore().getState().order.orderForm.orderFormId

    sendMetric({
      action: MetricAction.PaymentsSwitchTerminalClick,
      showcase: MetricShowcase.Payments,
      cancellation_status: getReduxStore().getState().order.cancellationStatus,
      payment_methods: orderForm.paymentData.payments,
    })
    await getReduxStore().dispatch(cancelTransaction(false))

    removeFromLocalStorage('LOCAL_STORAGE_CODE_KEY')

    await getReduxStore().dispatch(setShouldStartTransaction(true))

    await getReduxStore().dispatch(startTransaction(orderFormId))

    await getReduxStore().dispatch(setOrderActive(true))

    setIsChanging(false)
  }

  if (paymentApp) {
    paymentApp.appProps = {
      ...paymentApp.appProps,
      handleResetTransaction: handleResetTransaction,
    }
  }

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      className={csx({ height: 'full', padding: '$space-8' })}
    >
      {paymentApp && (
        <PaymentApp
          appName={paymentApp.appName}
          appProps={paymentApp.appProps}
        />
      )}

      {redirectUrl && <PaymentRedirect redirectUrl={redirectUrl} />}

      <FlexSpacer />

      <Loading>
        {isCancelling || isChanging ? (
          <FormattedMessage id={loadingMessage} />
        ) : isCancelling && !isChanging ? (
          <FormattedMessage id="placeorder.cancelling.title" />
        ) : (
          <FormattedMessage id={loadingMessage} />
        )}
      </Loading>

      <FlexSpacer />

      {!isCancelling && (
        <Button
          data-cy="cancel-order"
          variant="tertiary"
          onClick={showCancelModal}
          className={csx({ marginBottom: '$space-8' })}
        >
          <Text tone="secondary" variant="title1">
            <FormattedMessage id="placeorder.cancel.button" />
          </Text>
        </Button>
      )}

      {dismissDialogModalState.open && (
        <BottomModal state={dismissDialogModalState}>
          <OrderCancelModal
            onCancel={handleCancelTransaction}
            onGiveUp={() => dismissDialogModalState.toggle()}
          />
        </BottomModal>
      )}
    </Flex>
  )
}

export default PlacingOrder
